import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate, initials, creditsLabel, calcAge, fmtTimestamp } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { StatusBadge } from "@/components/StatusBadge";
import { DogDialog } from "@/components/dialogs/DogDialog";
import { CompositePrestationDialog } from "@/components/dialogs/CompositePrestationDialog";
import { EditSessionDialog } from "@/components/dialogs/EditSessionDialog";
import { PackageDialog } from "@/components/dialogs/PackageDialog";
import { CompleteDialog } from "@/components/dialogs/CompleteDialog";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { ProfessionalRecord } from "@/components/professional/ProfessionalRecord";
import { LessonsTab } from "@/components/professional/LessonDiary";
import { MonitoringTab } from "@/components/professional/Monitoring";
import { ClientDeliveriesTab } from "@/components/professional/ClientDeliveries";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, Plus, GraduationCap, Package as PkgIcon, Coins, CheckCircle2, XCircle, RotateCcw, Activity, CreditCard, ExternalLink, AlertTriangle, Trash2, ShieldAlert, ClipboardList, BookOpen, Eye, LineChart, Send } from "lucide-react";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { toast } from "sonner";

const iconFor = (kind) => ({ session: Activity, package: PkgIcon, package_exhausted: PkgIcon, payment: CreditCard }[kind] || Activity);

const val = (x) => (x !== undefined && x !== null && String(x).trim() !== "" ? String(x) : "Non indicato");
const Info = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-muted-foreground text-xs">{label}</div>
    <div className="font-medium break-words">{children}</div>
  </div>
);
const HEALTH = [
  ["health_alert", "Informazione sanitaria importante"],
  ["current_health_conditions", "Patologie / condizioni attuali"],
  ["medical_history", "Storia sanitaria rilevante"],
  ["surgeries_injuries", "Interventi / traumi importanti"],
  ["current_medications", "Farmaci / terapie in corso"],
  ["allergies_intolerances", "Allergie / intolleranze"],
  ["veterinary_restrictions", "Indicazioni / limitazioni veterinarie"],
];

export default function DogDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { guard } = useOnlineGuard();
  const [data, setData] = useState(null);
  const [editDog, setEditDog] = useState(false);
  const [addLez, setAddLez] = useState(false);
  const [addPkg, setAddPkg] = useState(false);
  const [complete, setComplete] = useState(null);
  const [editSess, setEditSess] = useState(null);
  const [editV2, setEditV2] = useState(null);
  const [tab, setTabState] = useState("panoramica");
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    setTabState(t === "professional" ? "scheda" : t === "lezioni" ? "lezioni" : t === "monitoraggio" ? "monitoraggio" : t === "consegne" ? "consegne" : "panoramica");
  }, [searchParams]);
  const setTab = (t) => setSearchParams(t === "scheda" ? { tab: "professional" } : t === "lezioni" ? { tab: "lezioni" } : t === "monitoraggio" ? { tab: "monitoraggio" } : t === "consegne" ? { tab: "consegne" } : {});
  const deliveryGuardRef = useRef(null);
  const [navLeave, setNavLeave] = useState(null);
  const guardedSetTab = (t) => {
    const g = deliveryGuardRef.current;
    if (g && g.isDirty()) setNavLeave({ go: () => setTab(t) });
    else setTab(t);
  };
  const [safetyRisks, setSafetyRisks] = useState([]);
  const [diaryStatus, setDiaryStatus] = useState({});
  useEffect(() => { apiGet(`/lesson-diaries/session-status`).then(({ data }) => setDiaryStatus(data || {})).catch(() => {}); }, [id, data]);
  const openDiary = (sid) => setSearchParams({ tab: "lezioni", session: sid });
  const diaryLabel = (st) => (st === "completed" ? "Apri diario" : st === "draft" ? "Continua diario" : "Compila diario");
  const focus = searchParams.get("focus") || "";
  const focusId = focus.includes(":") ? focus.split(":")[1] : "";

  useEffect(() => {
    apiGet(`/dogs/${id}/professional-items`)
      .then(({ data }) => setSafetyRisks(data.filter((i) => i.item_type === "risk" && !i.archived_at && i.payload?.show_safety_alert && i.status === "active")))
      .catch(() => {});
  }, [id, tab]);

  const load = useCallback(() => {
    apiGet(`/dogs/${id}`).then(({ data }) => setData(data)).catch((e) => { if (!e.isOffline) toast.error(apiErr(e)); });
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusId || !data || tab !== "panoramica") return;
    const t = setTimeout(() => {
      const el = document.getElementById(`chrono-item-${focusId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    return () => clearTimeout(t);
  }, [focusId, data, tab]);

  const cancelSession = async (sid) => {
    try { await api.patch(`/sessions/${sid}/status`, { status: "Annullata" }); toast.success("Prestazione annullata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const revertSession = async (sid) => {
    try { await api.patch(`/sessions/${sid}/status`, { status: "Pianificata" }); toast.success("Prestazione riportata a pianificata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const deleteSession = async (sid) => {
    try { await api.delete(`/sessions/${sid}`); toast.success("Prestazione eliminata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const [cancel, setCancel] = useState({ open: false, id: null, reason: "" });
  const doCancel = async () => {
    try {
      await api.patch(`/sessions/${cancel.id}/status`, { status: "Annullata", reason: cancel.reason });
      toast.success("Prestazione annullata");
      setCancel({ open: false, id: null, reason: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteDog = async () => {
    try { await api.delete(`/dogs/${data.dog.id}`); toast.success("Cane eliminato"); nav("/clienti"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setDogStato = async (stato) => {
    try { await api.patch(`/dogs/${data.dog.id}/stato`, { stato }); toast.success(stato === "Concluso" ? "Percorso archiviato" : "Percorso riattivato"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <p className="text-muted-foreground">Caricamento…</p>;
  const d = data.dog;
  const s = data.summary;
  const ap = s.pacchetto_attivo;
  const healthItems = HEALTH.filter(([k]) => d[k] && String(d[k]).trim());

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Indietro</button>

      {d.health_alert && String(d.health_alert).trim() && (
        <div data-testid="dog-health-alert" className="rounded-2xl border border-[#F5C6A5] bg-[#FFF3E9] p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-[#C2410C] shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold text-[#9A3412]">Informazione sanitaria importante</div>
            <div className="text-sm text-[#7C2D12] break-words">{d.health_alert}</div>
          </div>
        </div>
      )}

      {safetyRisks.length > 0 && (
        <button type="button" data-testid="dog-safety-alert" onClick={() => guardedSetTab("scheda")}
          className="w-full text-left rounded-2xl border border-[#E7B7B7] bg-[#FDECEC] p-4 flex items-start gap-3 hover:bg-[#FBE3E3] transition-colors">
          <ShieldAlert className="h-5 w-5 text-[#C62828] shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[#8E1B1B]">{safetyRisks.length === 1 ? "Rischio di sicurezza attivo" : `${safetyRisks.length} rischi di sicurezza attivi`}</div>
            {safetyRisks.length === 1
              ? <div className="text-sm text-[#7A1B1B] break-words">{safetyRisks[0].title}{safetyRisks[0].payload?.safety_measures ? ` — ${safetyRisks[0].payload.safety_measures}` : ""}</div>
              : <div className="text-sm text-[#7A1B1B] break-words">{safetyRisks.map((r) => r.title).join(" · ")}</div>}
          </div>
          <span className="text-sm font-semibold text-[#8E1B1B] shrink-0 self-center">Mostra →</span>
        </button>
      )}

      {/* Top-level tabs — 2×2 grid on mobile (all four visible, no horizontal scroll), single row on desktop */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-2 border-b border-border sticky z-30 bg-background/95 backdrop-blur" style={{ top: "calc(4rem + env(safe-area-inset-top))" }} data-testid="dog-tabs">
        <button data-testid="tab-panoramica" onClick={() => guardedSetTab("panoramica")} className={`inline-flex items-center justify-center sm:justify-start gap-1 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "panoramica" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>Panoramica</button>
        <button data-testid="tab-lezioni" onClick={() => guardedSetTab("lezioni")} className={`inline-flex items-center justify-center sm:justify-start gap-1 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "lezioni" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}><GraduationCap className="h-4 w-4 shrink-0" />Lezioni</button>
        <button data-testid="tab-scheda" onClick={() => guardedSetTab("scheda")} className={`inline-flex items-center justify-center sm:justify-start gap-1 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "scheda" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}><ClipboardList className="h-4 w-4 shrink-0" />Scheda professionale</button>
        <button data-testid="tab-monitoraggio" onClick={() => guardedSetTab("monitoraggio")} className={`inline-flex items-center justify-center sm:justify-start gap-1 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "monitoraggio" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}><LineChart className="h-4 w-4 shrink-0" />Monitoraggio</button>
        <button data-testid="tab-consegne" onClick={() => guardedSetTab("consegne")} className={`inline-flex items-center justify-center sm:justify-start gap-1 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "consegne" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}><Send className="h-4 w-4 shrink-0" />Consegne</button>
      </div>

      {tab === "scheda" && (
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="dog-compact-header">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-primary/10 text-primary font-extrabold shrink-0">{initials(d.nome)}</span>
            <div className="min-w-0">
              <div className="font-head text-lg font-bold truncate">{d.nome}</div>
              <div className="text-xs text-muted-foreground truncate">{[d.razza, d.owner_name].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {d.percorso && <span className="text-xs font-semibold hidden sm:inline">{d.percorso}</span>}
              <StatusBadge value={d.stato} testid="compact-stato-badge" />
            </div>
          </div>
        </div>
      )}

      {tab === "lezioni" && <LessonsTab dogId={d.id} />}

      {tab === "scheda" && <ProfessionalRecord dogId={d.id} onRisks={setSafetyRisks} />}

      {tab === "monitoraggio" && <MonitoringTab dogId={d.id} />}

      {tab === "consegne" && <ClientDeliveriesTab dogId={d.id} guardRef={deliveryGuardRef} />}

      {tab === "panoramica" && (<>
      {/* Summary card */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="dog-summary">
        <div className="flex items-start gap-4">
          <span className="grid place-items-center h-16 w-16 rounded-2xl bg-primary/10 text-primary font-extrabold text-xl shrink-0">{initials(d.nome)}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-head text-2xl font-extrabold">{d.nome}</h1>
              {d.tipo_caso === "Cane personale" && <span className="text-xs rounded-full bg-secondary px-2 py-0.5 font-semibold">Cane personale</span>}
            </div>
            <div className="text-sm text-muted-foreground">{[d.razza, d.sesso].filter(Boolean).join(" · ")}</div>
            <button onClick={() => nav(`/clienti/${d.owner_id}`)} className="text-sm text-primary font-semibold hover:underline">{d.owner_name}</button>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {d.percorso && <span className="text-sm font-semibold">{d.percorso}</span>}
              <StatusBadge value={d.stato} testid="dog-stato-badge" />
            </div>
            {d.problema && <p className="mt-1 text-sm text-muted-foreground">Problema: {d.problema}</p>}
          </div>
          <Button variant="outline" size="sm" data-testid="edit-dog-btn" onClick={() => setEditDog(true)} className="rounded-full"><Pencil className="h-4 w-4" /></Button>
        </div>

        {ap && (
          <div className="mt-4 rounded-xl border border-border p-4 bg-secondary/30" data-testid="dog-package-bar">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <span className="font-semibold flex items-center gap-1.5"><Coins className="h-4 w-4 text-primary" />Pacchetto {fmtEUR(ap.total_price)}</span>
              <StatusBadge value={ap.status} testid="dog-credit-badge" />
            </div>
            <Progress value={ap.total_credits ? (ap.used_credits / ap.total_credits) * 100 : 0} className="h-2.5" />
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <Info label="Valore">{fmtEUR(ap.total_price)}</Info>
              <Info label="Incassato">{fmtEUR(ap.paid_amount)}</Info>
              {ap.client_credit_applied > 0 && <Info label="Credito cliente">{fmtEUR(ap.client_credit_applied)}</Info>}
              <Info label="Da incassare">{fmtEUR(ap.outstanding)}</Info>
              <Info label="Crediti">{ap.used_credits}/{ap.total_credits} utilizzati · {ap.remaining_credits} residui</Info>
              <div><div className="text-muted-foreground text-xs">Stato pagamento</div><div className="mt-0.5"><StatusBadge value={ap.payment_status} testid="dog-payment-badge" /></div></div>
              <Info label="Aperto">{fmtDate(ap.opening_date)}</Info>
            </div>
          </div>
        )}

        {!ap && (
          <div className="mt-4 rounded-xl border border-dashed border-border p-4 bg-secondary/20 text-sm text-muted-foreground" data-testid="dog-no-package">
            Nessun pacchetto attivo.
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><div className="text-muted-foreground text-xs">Ultima lezione</div><div className="font-semibold">{fmtDate(s.ultima_prestazione)}</div></div>
          <div><div className="text-muted-foreground text-xs">Prossima</div><div className="font-semibold">{fmtDate(s.prossimo_appuntamento)}</div></div>
          {!ap && <div><div className="text-muted-foreground text-xs">Crediti residui</div><div className="font-semibold tabular">{s.crediti_residui}</div></div>}
          {!ap && <div><div className="text-muted-foreground text-xs">Da incassare</div><div className="font-semibold tabular">{fmtEUR(s.outstanding)}</div></div>}
        </div>
        {d.drive_url && <a href={d.drive_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-primary font-semibold"><ExternalLink className="h-4 w-4" />Cartella Drive</a>}
      </div>

      {/* Frequent actions — immediately accessible after the summary */}
      <div className="flex gap-2 flex-wrap" data-testid="dog-primary-actions">
        <Button data-testid="dog-add-lezione" onClick={guard(() => setAddLez(true))} className="rounded-full"><GraduationCap className="h-4 w-4 mr-1" />Registra lezione</Button>
        <Button variant="outline" data-testid="dog-add-pkg" onClick={guard(() => setAddPkg(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Pacchetto</Button>
      </div>

      {/* Read-only profile */}
      <section className="grid gap-4 md:grid-cols-2 items-start">
        <CollapsibleSection id="dog-dati" testid="dog-profile-dati" card cardClass="rounded-2xl border border-border bg-card p-5" title="Dati del cane" titleClass="font-head text-base font-bold">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Razza">{val(d.razza)}</Info>
            <Info label="Sesso">{val(d.sesso)}</Info>
            <Info label="Data di nascita">{d.data_nascita ? fmtDate(d.data_nascita) : "Non indicato"}</Info>
            <Info label="Età" testid="dog-eta-calc">{calcAge(d.data_nascita) || val(d.eta_testo)}</Info>
            <Info label="Peso">{d.peso && String(d.peso).trim() ? `${d.peso} kg` : "Non indicato"}</Info>
            <Info label="Sterilizzato/castrato">{val(d.sterilizzato)}</Info>
            <Info label="Microchip">{val(d.microchip)}</Info>
            {d.foto_url && String(d.foto_url).trim() && (
              <div className="col-span-2">
                <div className="text-muted-foreground text-xs mb-1">Foto</div>
                <img src={d.foto_url} alt={d.nome} className="h-28 w-28 rounded-xl object-cover border border-border" />
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection id="dog-percorso" testid="dog-profile-percorso" card cardClass="rounded-2xl border border-border bg-card p-5" title="Percorso" titleClass="font-head text-base font-bold">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Tipo caso">{val(d.tipo_caso)}</Info>
            <Info label="Percorso / servizio">{val(d.percorso)}</Info>
            <div>
              <div className="text-muted-foreground text-xs">Stato percorso</div>
              <div className="mt-0.5"><StatusBadge value={d.stato} /></div>
            </div>
            <Info label="Data primo incontro">{d.data_primo_incontro ? fmtDate(d.data_primo_incontro) : "Non indicato"}</Info>
            <Info label="Problema principale"><span className="col-span-2">{val(d.problema)}</span></Info>
            <div className="col-span-2"><Info label="Note generali">{val(d.note)}</Info></div>
          </div>
        </CollapsibleSection>
      </section>

      <section className="grid gap-4 md:grid-cols-2 items-start">
        <CollapsibleSection id="dog-salute" testid="dog-profile-salute" card cardClass="rounded-2xl border border-border bg-card p-5" title="Salute e informazioni veterinarie" titleClass="font-head text-base font-bold"
          action={d.health_updated_at ? <span className="text-[11px] text-muted-foreground shrink-0">Aggiornato {fmtTimestamp(d.health_updated_at)}</span> : null}>
          {healthItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna informazione sanitaria registrata.</p>
          ) : (
            <div className="space-y-3 text-sm">
              {healthItems.map(([k, label]) => (
                <div key={k}>
                  <div className="text-muted-foreground text-xs">{label}</div>
                  <div className="font-medium break-words whitespace-pre-line">{d[k]}</div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        <CollapsibleSection id="dog-riferimenti" testid="dog-profile-riferimenti" card cardClass="rounded-2xl border border-border bg-card p-5" title="Informazioni e riferimenti" titleClass="font-head text-base font-bold">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <Info label="Veterinario">{val(d.veterinario)}</Info>
            <Info label="Veterinario comportamentalista">{val(d.veterinario_comportamentalista)}</Info>
            <div>
              <div className="text-muted-foreground text-xs">Cartella Drive</div>
              {d.drive_url && String(d.drive_url).trim() ? (
                <a href={d.drive_url} target="_blank" rel="noreferrer" className="font-medium text-primary inline-flex items-center gap-1 break-all"><ExternalLink className="h-4 w-4 shrink-0" />Apri cartella</a>
              ) : <div className="font-medium">Non indicato</div>}
            </div>
          </div>
        </CollapsibleSection>
      </section>

      {/* Chronology */}
      <CollapsibleSection id="dog-cronologia" testid="dog-cronologia" title="Cronologia" titleClass="font-head text-lg font-bold" forceOpen={!!focusId}>
        <div className="space-y-2" data-testid="dog-chronology">
          {data.chronology.map((e, i) => {
            const Icon = iconFor(e.kind);
            const it = e.data;
            const isFocus = focusId && it && it.id === focusId;
            return (
              <div key={i} id={it && it.id ? `chrono-item-${it.id}` : undefined}
                className={`rounded-2xl border bg-card p-3.5 flex items-start gap-3 transition-colors ${isFocus ? "border-accent ring-2 ring-accent/50" : "border-border"}`}>
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-secondary text-foreground shrink-0"><Icon className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  {e.kind === "session" && (
                    <>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold">{it.service_type}{it.is_free ? " · gratuita" : ""}</span>
                        <StatusBadge value={it.status} />
                      </div>
                      <div className="text-sm text-muted-foreground">{fmtDate(it.date)}{it.start_time ? ` · ${it.start_time}` : ""} · {fmtDuration(it.duration_minutes) || "—"}
                        {it.status === "Svolta" && it.credits_consumed > 0 && ` · ${creditsLabel(it.credits_consumed)}`}</div>
                      {it.billable && (
                        <div className="mt-1 flex gap-3 flex-wrap text-xs" data-testid={`chrono-fin-${it.id}`}>
                          <span className="text-muted-foreground">Valore {fmtEUR(it.value)}</span>
                          <span className="text-muted-foreground">Incassato {fmtEUR(it.real_money_paid)}</span>
                          {it.client_credit_applied > 0 && <span className="text-[#1E5E2B]">Credito cliente {fmtEUR(it.client_credit_applied)}</span>}
                          {it.fin_outstanding > 0
                            ? <span className="text-[#8D6E63] font-semibold">Da incassare {fmtEUR(it.fin_outstanding)}</span>
                            : <span className="text-[#1E5E2B] font-semibold">Pagato</span>}
                        </div>
                      )}
                      {it.status === "Pianificata" && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          <Button size="sm" className="rounded-full" data-testid={`chrono-complete-${it.id}`} onClick={() => setComplete(it)}><CheckCircle2 className="h-4 w-4 mr-1" />Svolta</Button>
                          <Button size="sm" variant="outline" className="rounded-full" data-testid={`chrono-edit-${it.id}`} onClick={() => (it.engine === "v2" ? setEditV2(it) : setEditSess(it))}><Pencil className="h-4 w-4 mr-1" />Modifica</Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="rounded-full" data-testid={`chrono-cancel-${it.id}`}><XCircle className="h-4 w-4 mr-1" />Annulla</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Annullare questa prestazione?</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div>
                                    <span className="block">Cane: <b>{d.nome}</b></span>
                                    <span className="block">Servizio: <b>{it.service_type}</b></span>
                                    <span className="block">Data: <b>{fmtDate(it.date)}{it.start_time ? ` · ${it.start_time}` : ""}</b></span>
                                    <span className="block mt-2">La prestazione resterà nello storico con stato <b>Annullata</b> e non consumerà crediti del pacchetto.</span>
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Indietro</AlertDialogCancel>
                                <AlertDialogAction data-testid={`chrono-cancel-confirm-${it.id}`} onClick={() => cancelSession(it.id)}>Conferma annullamento</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                      {(it.status === "Svolta" || it.status === "Annullata") && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                          {(it.status === "Svolta" || diaryStatus[it.id]) && (
                            <Button size="sm" variant="outline" className="rounded-full" data-testid={`chrono-diary-${it.id}`} onClick={() => openDiary(it.id)}>
                              <BookOpen className="h-4 w-4 mr-1" />{diaryLabel(diaryStatus[it.id])}
                            </Button>
                          )}
                          {it.engine === "v2" && it.status === "Svolta" && (
                            <Button size="sm" variant="outline" className="rounded-full" data-testid={`chrono-view-${it.id}`} onClick={() => setEditV2(it)}><Eye className="h-4 w-4 mr-1" />Dettaglio</Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="rounded-full" data-testid={`chrono-revert-${it.id}`}><RotateCcw className="h-4 w-4 mr-1" />Riporta a pianificata</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Riportare questa prestazione a Pianificata?</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div>
                                    <span className="block">Cane: <b>{d.nome}</b></span>
                                    <span className="block">Servizio: <b>{it.service_type}</b></span>
                                    <span className="block">Data: <b>{fmtDate(it.date)}{it.start_time ? ` · ${it.start_time}` : ""}</b></span>
                                    {it.status === "Svolta" && (it.paid || 0) > 0
                                      ? <span className="block mt-2 text-[#8D6E63]">Questa prestazione ha un movimento economico collegato (pagamento o credito cliente). Prima di riportarla a Pianificata devi risolvere il pagamento o il credito cliente associato.</span>
                                      : it.status === "Svolta"
                                      ? <span className="block mt-2">I crediti consumati ({it.credits_consumed}) verranno ripristinati sul pacchetto.</span>
                                      : <span className="block mt-2">La prestazione tornerà <b>Pianificata</b>. Nessun credito verrà modificato.</span>}
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Indietro</AlertDialogCancel>
                                {!(it.status === "Svolta" && (it.paid || 0) > 0) && (
                                  <AlertDialogAction data-testid={`chrono-revert-confirm-${it.id}`} onClick={() => revertSession(it.id)}>Riporta a pianificata</AlertDialogAction>
                                )}
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {it.status === "Svolta" && (
                            <Button size="sm" variant="outline" className="rounded-full text-[#C62828] border-[#E7B7B7]" data-testid={`chrono-cancel-${it.id}`} onClick={() => setCancel({ open: true, id: it.id, reason: "" })}>
                              <XCircle className="h-4 w-4 mr-1" />Annulla prestazione
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="mt-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="rounded-full text-destructive" data-testid={`chrono-delete-${it.id}`}><Trash2 className="h-4 w-4 mr-1" />Elimina</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminare definitivamente la prestazione?</AlertDialogTitle>
                              <AlertDialogDescription asChild>
                                <div>
                                  <span className="block">Servizio: <b>{it.service_type}</b> · {fmtDate(it.date)}{it.start_time ? ` · ${it.start_time}` : ""}</span>
                                  {it.status === "Svolta"
                                    ? <span className="block mt-2 text-destructive font-semibold">Attenzione: prestazione Svolta. Cambieranno i KPI (prestazioni, ore, valore). Irreversibile.</span>
                                    : <span className="block mt-2">Rimozione definitiva dallo storico. Irreversibile.</span>}
                                  <span className="block mt-1 text-xs text-muted-foreground">Con pagamenti collegati, crediti consumati o evento Calendar attivo l'eliminazione verrà bloccata.</span>
                                </div>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction data-testid={`chrono-delete-confirm-${it.id}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => deleteSession(it.id)}>Elimina definitivamente</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </>
                  )}
                  {e.kind === "package" && (
                    <>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold">Pacchetto {fmtEUR(it.total_price)} · {it.total_credits} crediti</span>
                        <StatusBadge value={it.payment_status} />
                      </div>
                      <div className="text-sm text-muted-foreground">Aperto {fmtDate(it.opening_date)}</div>
                      <div className="mt-1 flex gap-3 flex-wrap text-xs" data-testid={`chrono-pkg-fin-${it.id}`}>
                        <span className="text-muted-foreground">Valore {fmtEUR(it.total_price)}</span>
                        <span className="text-muted-foreground">Incassato {fmtEUR(it.paid_amount)}</span>
                        {it.client_credit_applied > 0 && <span className="text-[#1E5E2B]">Credito cliente {fmtEUR(it.client_credit_applied)}</span>}
                        {it.outstanding > 0
                          ? <span className="text-[#8D6E63] font-semibold">Residuo {fmtEUR(it.outstanding)}</span>
                          : <span className="text-[#1E5E2B] font-semibold">Pagato</span>}
                      </div>
                    </>
                  )}
                  {e.kind === "package_exhausted" && (
                    <>
                      <span className="font-semibold">Pacchetto esaurito</span>
                      <div className="text-sm text-muted-foreground">{fmtDate(it.exhaustion_date)}</div>
                    </>
                  )}
                  {e.kind === "payment" && (
                    <>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold">{it.type} {fmtEUR(it.amount)}</span>
                        <span className="text-xs text-muted-foreground">{it.method}</span>
                      </div>
                      <div className="text-sm text-muted-foreground">{fmtDate(it.date)}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {data.chronology.length === 0 && <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>}
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="dog-gestione" testid="dog-danger-zone" card cardClass="rounded-2xl border border-destructive/30 bg-destructive/5 p-5" title="Gestione percorso" titleClass="font-head text-base font-bold">
        <div className="space-y-4">
        <div>
          {d.stato === "Concluso" ? (
            <>
              <p className="text-sm text-muted-foreground mb-3">Il percorso è archiviato (Concluso). Puoi riattivarlo senza modificare lo storico.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="reactivate-dog-btn" className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Riattiva percorso</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Riattivare il percorso di questo cane?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <span className="block">Cane: <b>{d.nome}</b></span>
                        <span className="block">Proprietario: <b>{d.owner_name || "—"}</b></span>
                        <span className="block mt-2">Lo stato del percorso tornerà <b>Attivo</b>. Nessun altro dato verrà modificato.</span>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Indietro</AlertDialogCancel>
                    <AlertDialogAction data-testid="reactivate-dog-confirm" onClick={() => setDogStato("Attivo")}>Riattiva percorso</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">Archivia il percorso mantenendo tutto lo storico (prestazioni, pacchetti, pagamenti, crediti).</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="archive-dog-btn" className="rounded-full">Archivia percorso</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archiviare il percorso di questo cane?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <span className="block">Cane: <b>{d.nome}</b></span>
                        <span className="block">Proprietario: <b>{d.owner_name || "—"}</b></span>
                        <span className="block mt-2">Il cane resterà nel gestionale con tutto lo storico, ma il percorso verrà impostato su <b>Concluso</b>.</span>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Indietro</AlertDialogCancel>
                    <AlertDialogAction data-testid="archive-dog-confirm" onClick={() => setDogStato("Concluso")}>Archivia percorso</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>

        <div className="border-t border-destructive/20 pt-4">
          <h2 className="font-head text-base font-bold text-destructive mb-2">Elimina cane</h2>
          {data.chronology.length > 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="dog-delete-blocked">Questo cane contiene dati storici (prestazioni, pacchetti o pagamenti) e non può essere eliminato definitivamente. Per archiviarlo, usa <b>Archivia percorso</b> qui sopra.</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-3">Nessun dato storico collegato. Puoi eliminare definitivamente questo cane.</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="delete-dog-btn" className="rounded-full">Elimina cane</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminare definitivamente questo cane?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <span className="block">Cane: <b>{d.nome}</b></span>
                        <span className="block">Proprietario: <b>{d.owner_name || "—"}</b></span>
                        <span className="block mt-2">L'operazione è irreversibile.</span>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Indietro</AlertDialogCancel>
                    <AlertDialogAction data-testid="delete-dog-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={deleteDog}>Elimina definitivamente</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
        </div>
      </CollapsibleSection>
      </>)}

      <DogDialog open={editDog} onOpenChange={setEditDog} dog={d} onSaved={load} />
      <AlertDialog open={!!navLeave} onOpenChange={(v) => { if (!v) setNavLeave(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>La consegna ha modifiche non salvate. Vuoi riprovare a salvare o uscire senza salvare?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" className="rounded-full" data-testid="nav-guard-retry" onClick={async () => {
              const g = deliveryGuardRef.current;
              try { await g.flush(); if (!g.isDirty()) { const go = navLeave.go; setNavLeave(null); go(); } else toast.error("Salvataggio non riuscito. Riprova."); }
              catch { toast.error("Salvataggio non riuscito. Riprova."); }
            }}>Riprova</Button>
            <Button variant="destructive" className="rounded-full" data-testid="nav-guard-discard" onClick={() => {
              const g = deliveryGuardRef.current; if (g) g.discard();
              const go = navLeave?.go; setNavLeave(null); if (go) go();
            }}>Esci senza salvare</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={cancel.open} onOpenChange={(v) => !v && setCancel({ open: false, id: null, reason: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare la prestazione svolta?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <span className="block">La prestazione passerà direttamente da <b>Svolta</b> a <b>Annullata</b>.</span>
                <span className="block mt-1">Gli eventuali crediti pacchetto consumati verranno ripristinati. Prestazioni e ore erogate verranno ricalcolate.</span>
                <span className="block mt-1 text-xs text-muted-foreground">Se esiste un pagamento collegato diretto, l'annullamento verrà bloccato.</span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <label className="text-sm font-medium">Motivo annullamento (opzionale)</label>
            <input data-testid="chrono-cancel-reason" value={cancel.reason} onChange={(e) => setCancel((c) => ({ ...c, reason: e.target.value }))}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="es. cliente ha disdetto" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Indietro</AlertDialogCancel>
            <AlertDialogAction data-testid="chrono-cancel-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doCancel}>Annulla prestazione</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CompositePrestationDialog open={addLez} onOpenChange={setAddLez} mode="lezione" presetDogId={d.id} presetOwnerId={d.owner_id} onSaved={load} />
      <CompositePrestationDialog open={!!editV2} onOpenChange={(v) => !v && setEditV2(null)} editSession={editV2} onSaved={load} />
      <PackageDialog open={addPkg} onOpenChange={setAddPkg} presetOwnerId={d.owner_id} presetDogId={d.id} onSaved={load} />
      <CompleteDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} session={complete ? { ...complete, dog_names: [d.nome] } : null} onDone={load} />
      <EditSessionDialog open={!!editSess} onOpenChange={(v) => !v && setEditSess(null)} session={editSess} dogNames={[d.nome]} ownerName={d.owner_name} onSaved={load} />
    </div>
  );
}
